import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../PageHeader";
import { useEffect, useState } from "react";
import Loader from "../../common/Loader";
import { triggerAlert, simpleAlert } from "../../utils";
import { useForm } from "react-hook-form";
import React from "react";
import { _get } from "../../utils/APIClient";

/**
 * Reports / Revenue — month-wise revenue from paid AgentSubscriptionInvoice
 * rows. Backend `/api/admin/monthwise_revenue_view` returns the last 12 months
 * as `{revenue_list: {"YYYY-MM": total, ...}}` wrapped in the standard
 * `{results, message, error_code}` envelope. The year selector filters the
 * returned window client-side.
 */

const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const CATEGORY_LABELS = {
  avatar: "Avatar",
  calling_v2: "Calling",
  workflow: "Workflow",
  phone_number: "Phone Numbers",
  unknown: "Uncategorized",
};

function Revenue() {
  document.title = "Digital Employees - Revenue";
  const mainHeading = "User Revenue";
  const heading = "Reports / User Revenue";

  const currentYear = new Date().getFullYear();
  const startYear = 2023;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [yearOptions, setYearOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [categoryBreakdown, setCategoryBreakdown] = useState({});
  const [categoryKeys, setCategoryKeys] = useState([]);

  useEffect(() => {
    const opts = [];
    for (let y = currentYear; y >= startYear; y--) opts.push(y);
    setYearOptions(opts);
  }, [currentYear]);

  const buildRowsFromMap = (revenueMap, yearFilter) => {
    // revenueMap is { "YYYY-MM": total }. Group into {month: {year: total}}
    // filtered by yearFilter ("all" or a specific year string).
    const byMonth = {};
    Object.entries(revenueMap || {}).forEach(([ym, total]) => {
      const [y, m] = ym.split("-");
      if (yearFilter && yearFilter !== "all" && String(y) !== String(yearFilter)) return;
      const monthNum = parseInt(m, 10);
      if (!byMonth[monthNum]) byMonth[monthNum] = { month: monthNum, byYear: {} };
      byMonth[monthNum].byYear[y] = Number(total || 0);
    });
    return Object.values(byMonth).sort((a, b) => a.month - b.month);
  };

  const yearsInRows = (rowList) => {
    const set = new Set();
    rowList.forEach((r) => Object.keys(r.byYear).forEach((y) => set.add(y)));
    return Array.from(set).sort();
  };

  const filterBreakdown = (raw, yearFilter) => {
    // raw: {"YYYY-MM": {category: total, ...}}
    const out = {};
    const cats = new Set();
    Object.entries(raw || {}).forEach(([ym, catMap]) => {
      const [y] = ym.split("-");
      if (yearFilter && yearFilter !== "all" && String(y) !== String(yearFilter)) return;
      out[ym] = catMap;
      Object.keys(catMap || {}).forEach((c) => cats.add(c));
    });
    return { filtered: out, categoryKeys: Array.from(cats).sort() };
  };

  const saveSubmit = async (data) => {
    const year = data.year;
    if (!year) {
      simpleAlert("Select the Year!!");
      return;
    }
    try {
      setIsLoading(true);
      const response = await _get(`/api/admin/monthwise_revenue_view?year=${year}`);
      const payload = response?.data?.results;
      const revenueMap = payload?.revenue_list || {};
      const built = buildRowsFromMap(revenueMap, year);
      setRows(built);
      setSelectedYear(year);
      setShowTable(built.length > 0);
      const bd = filterBreakdown(payload?.category_breakdown || {}, year);
      setCategoryBreakdown(bd.filtered);
      setCategoryKeys(bd.categoryKeys);
    } catch (error) {
      setRows([]);
      setShowTable(false);
      setCategoryBreakdown({});
      setCategoryKeys([]);
      triggerAlert("error", "Oops...", "Something went wrong..");
    } finally {
      setIsLoading(false);
    }
  };

  const downloadCsv = () => {
    if (!rows.length) return;
    const years = yearsInRows(rows);
    const header = ["Month", ...years, ...categoryKeys.map(
      (k) => `${CATEGORY_LABELS[k] || k} (all years)`
    )];
    const catTotals = {};
    Object.values(categoryBreakdown).forEach((catMap) => {
      Object.entries(catMap || {}).forEach(([c, v]) => {
        catTotals[c] = (catTotals[c] || 0) + Number(v || 0);
      });
    });
    const body = rows.map((r) => {
      const line = [
        MONTH_LABELS[r.month - 1],
        ...years.map((y) => (r.byYear[y] != null ? Number(r.byYear[y]).toFixed(2) : "")),
        // Category columns only meaningful once per report — leave blank per row.
        ...categoryKeys.map(() => ""),
      ];
      return line;
    });
    const footer = [
      "TOTAL",
      ...years.map((y) => {
        const t = rows.reduce((acc, r) => acc + (r.byYear[y] || 0), 0);
        return t.toFixed(2);
      }),
      ...categoryKeys.map((c) => (catTotals[c] || 0).toFixed(2)),
    ];
    const csv = [header, ...body, footer]
      .map((line) => line.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `revenue-${selectedYear || "all"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const displayYears = yearsInRows(rows);

  return (
    <>
      <div id="layout-wrapper">
        <Header />
        <Sidebar />
        {isLoading && (
          <div className="loader-overlay text-white">
            <Loader />
          </div>
        )}
        <div className="main-content">
          <div className="page-content" style={{ backgroundColor: "#ededed" }}>
            <div className="container-fluid">
              <PageHeader mainHeading={mainHeading} heading={heading} />

              <div className="row mt-4">
                <div className="col-xl-12">
                  <div className="card-body">
                    <form onSubmit={handleSubmit(saveSubmit)} noValidate>
                      <div className="row g-3">
                        <div className="col-md-2">
                          <label htmlFor="year" className="form-label">Select a Year:</label>
                          <select
                            id="year"
                            className="form-control"
                            {...register("year", { required: "Year is required" })}
                          >
                            <option value="">Select a year</option>
                            <option value="all">All</option>
                            {yearOptions.map((y) => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                          {errors.year && (
                            <div style={{ color: "red", fontSize: "14px", marginTop: "5px" }}>
                              {errors.year.message}
                            </div>
                          )}
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">&nbsp;</label>
                          <div className="button-items d-flex gap-2">
                            <button type="submit" className="btn btn-primary w-md">Search</button>
                            {rows.length > 0 && (
                              <button
                                type="button"
                                className="btn btn-outline-secondary w-md"
                                onClick={downloadCsv}
                              >
                                Download CSV
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {showTable && categoryKeys.length > 0 && (
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <h6 className="mb-3">Revenue by Category</h6>
                        <table className="table table-bordered mb-0" style={{ width: "100%" }}>
                          <thead style={{ backgroundColor: "#014670", color: "#fff" }}>
                            <tr>
                              <th>Month</th>
                              {categoryKeys.map((c) => (
                                <th key={c}>{CATEGORY_LABELS[c] || c}</th>
                              ))}
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(categoryBreakdown)
                              .sort(([a], [b]) => a.localeCompare(b))
                              .map(([ym, catMap]) => {
                                const rowTotal = Object.values(catMap || {})
                                  .reduce((acc, v) => acc + Number(v || 0), 0);
                                return (
                                  <tr key={ym}>
                                    <td>{ym}</td>
                                    {categoryKeys.map((c) => (
                                      <td key={c}>
                                        {catMap && catMap[c] != null
                                          ? Number(catMap[c]).toFixed(2)
                                          : "—"}
                                      </td>
                                    ))}
                                    <td className="fw-bold">{rowTotal.toFixed(2)}</td>
                                  </tr>
                                );
                              })}
                            {/* Category grand-totals across all months in view */}
                            <tr>
                              <td className="fw-bold text-end">Grand Total</td>
                              {categoryKeys.map((c) => {
                                const t = Object.values(categoryBreakdown)
                                  .reduce((acc, catMap) => acc + Number(catMap?.[c] || 0), 0);
                                return (
                                  <td key={c} className="fw-bold">
                                    {t.toFixed(2)}
                                  </td>
                                );
                              })}
                              <td className="fw-bold">
                                {Object.values(categoryBreakdown)
                                  .reduce((acc, catMap) => acc + Object.values(catMap || {})
                                    .reduce((a, v) => a + Number(v || 0), 0), 0)
                                  .toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="row mt-5">
                <div className="col-12">
                  <div className="card-body">
                    {showTable ? (
                      <table className="table-responsive table-bordered table" style={{ width: "100%" }}>
                        <thead style={{ backgroundColor: "#014670", color: "#fff" }}>
                          <tr>
                            <th>ID</th>
                            <th>Month</th>
                            {displayYears.map((y) => <th key={y}>{y}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, i) => (
                            <tr key={`row-${row.month}`}>
                              <td>{i + 1}</td>
                              <td>{MONTH_LABELS[row.month - 1]}</td>
                              {displayYears.map((y) => (
                                <td key={`${row.month}-${y}`}>
                                  {row.byYear[y] != null ? Number(row.byYear[y]).toFixed(2) : "---"}
                                </td>
                              ))}
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={2} className="text-end fw-bold">Revenue Yearly:</td>
                            {displayYears.map((y) => {
                              const yearTotal = rows.reduce(
                                (acc, r) => acc + (r.byYear[y] || 0),
                                0
                              );
                              return (
                                <td key={`total-${y}`} className="fw-bold">
                                  {yearTotal.toFixed(2)}
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <table className="table-responsive table-bordered table" style={{ width: "100%" }}>
                        <thead style={{ backgroundColor: "#014670", color: "#fff" }}>
                          <tr>
                            <th>ID</th>
                            <th>Month</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td colSpan="9" className="text-center">No data available</td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      <div className="rightbar-overlay"></div>
    </>
  );
}

export default Revenue;
